/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import { API, Auth } from "aws-amplify";
import { Data } from "../../../types/MessageDialogParams";
import { STATE_DONE, STATE_NO_BUILDS } from "../../Authenticator/utils";

const apiName = "ApsBackend";

/* The Version of the APS API we are communicating with. The APS API uses semantic versioning
   vMAJOR.MINOR.PATCH. Doorway sends the version is expected in the Accept header. This version
   does not need to be updated each time APS changes the PATCH or MINOR versions, but any change to the
   MAJOR version will need to be handled appropriately (either Doorway updates it's MAJOR version or
   else APS provides backwards compatibility guarantees for older MAJOR versions).
*/
const apiVersion = "v1.0.0";

export interface Build {
  buildId: string;
  appName: string;
  iconUrl: string;
  buildLabel: string;
  state: string;
  updated: number;
  os: string;
}

const getHeaders = async () => {
  try {
    await Auth.currentAuthenticatedUser();
    const session = await Auth.currentSession();
    const jwtToken = session.getIdToken().getJwtToken();
    const accept = `application/vnd.aps.appshield.verimatrixcloud.net;version=${apiVersion}`;
    return { Authorization: jwtToken, Accept: accept };
  } catch (e) {
    Auth.signOut();
  }
};

const getApplications = async (orgId?: string, subscriptionType?: string) => {
  const headers = await getHeaders();
  const forOrg = orgId ? `&customer=${orgId}` : "";
  const forSubscriptionType = subscriptionType ? `&subscriptionType=${subscriptionType}` : "";
  const result = await API.get(
    apiName,
    `/applications?includeIcons=true${forOrg}${forSubscriptionType}`,
    {
      headers,
    }
  ).catch(() => {
    return null;
  });
  return result;
};

const setApplicationConfig = async (appId: string, config: any) => {
  const headers = await getHeaders();
  const result = await API.put(
    apiName,
    `/applications/${appId}/protection-configuration`,
    {
      headers,
      body: config
        ? {
            configuration: { ...config },
          }
        : {},
    }
  );
  return result;
};

const getBuilds = async (appId: string) => {
  const headers = await getHeaders();
  const result = await API.get(apiName, `/builds?app=${appId}`, {
    headers,
  }).catch(() => {
    return null;
  });
  return result;
};

const getBuildById = async (id: string) => {
  const headers = await getHeaders();
  const result = await API.get(apiName, `/builds/${id}`, {
    headers,
  });
  return result;
};

const executeProtection = async (build: Data) => {
  const headers = await getHeaders();
  const result = await API.patch(apiName, `/builds/${build.id}?cmd=protect`, {
    headers,
  });
  return result;
};

const abortProtection = async (build: Data) => {
  const headers = await getHeaders();
  const result = await API.patch(apiName, `/builds/${build.id}?cmd=cancel`, {
    headers,
  });
  return result;
};

const getBuildArtifactsInfo = async (buildId: string) => {
  const headers = await getHeaders();
  const result = await API.get(
    apiName,
    `/report/artifacts?buildId=${buildId}`,
    {
      headers,
    }
  );
  return result;
};

const sortByUpdated = (updatedA: Build, updatedB: Build) => {
  if (updatedA.updated < updatedB.updated) {
    return 1;
  } else if (updatedA.updated > updatedB.updated) {
    return -1;
  } else {
    return 0;
  }
};

const fetchApsBuilds = async () => {
  const recentBuilds: Build[] = [];
  const protectedBuilds: Build[] = [];
  const applications = await getApplications();

  if (applications) {
    for (const application of applications) {
      const builds = await getBuilds(application.id);

      if (builds) {
        const apsBuilds = [];
        for (const build of builds) {
          const apsBuild: Build = {
            buildId: build.id,
            appName: application.applicationName,
            iconUrl: application.iconUrl,
            buildLabel: build.label,
            state: build.state,
            updated: build.updated,
            os: application.os,
          };

          apsBuilds.push(apsBuild);
          if (apsBuild.state === STATE_DONE) {
            protectedBuilds.push(apsBuild);
          }
        }
        if (apsBuilds.length) {
          apsBuilds.sort((a, b) => sortByUpdated(a, b));
          recentBuilds.push(apsBuilds[0]);
        } else {
          recentBuilds.push({
            buildId: "",
            appName: application.applicationName,
            iconUrl: application.iconUrl,
            buildLabel: "",
            state: STATE_NO_BUILDS,
            updated: 0,
            os: application.os,
          });
        }
      }
    }
  }

  return { protectedBuilds: protectedBuilds, recentBuilds: recentBuilds };
};

export {
  fetchApsBuilds,
  getApplications,
  setApplicationConfig,
  getBuildArtifactsInfo,
  getBuildById,
  executeProtection,
  abortProtection,
};
